import { motion } from "framer-motion";
import { UserCheck, MonitorSmartphone, HeartHandshake } from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";
import { openWhatsApp } from "@/lib/whatsapp";

const POINTS = [
  { icon: UserCheck, label: "Infermiere accanto" },
  { icon: MonitorSmartphone, label: "Medico sul tablet" },
  { icon: HeartHandshake, label: "Familiare aggiornato" },
];

export default function ElderlySection() {
  return (
    <section id="anziani" className="bg-[#F6F4FA] py-20 sm:py-28" data-testid="elderly-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-10 items-center">
          {/* Image */}
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="relative lg:col-span-6"
          >
            <div className="overflow-hidden rounded-[2rem] lg:rounded-[2.5rem] shadow-[0_30px_80px_rgba(91,44,145,0.18)] ring-1 ring-white/60">
              <img
                src="/generated/anziani.png"
                alt="Anziana a casa con infermiere accanto e medico collegato sul tablet"
                className="w-full h-[360px] sm:h-[460px] object-cover"
                data-testid="elderly-image"
              />
            </div>
            <div className="absolute -bottom-5 left-4 right-4 flex flex-wrap justify-center gap-2 sm:gap-3">
              {POINTS.map((p) => {
                const Icon = p.icon;
                return (
                  <div
                    key={p.label}
                    className="flex items-center gap-2 rounded-full bg-white px-3.5 py-2 shadow-[0_12px_30px_rgba(91,44,145,0.14)]"
                  >
                    <Icon className="h-4 w-4 text-[#5B2C91]" />
                    <span className="text-xs font-semibold text-[#1F152E]">{p.label}</span>
                  </div>
                );
              })}
            </div>
          </motion.div>

          {/* Copy */}
          <div className="lg:col-span-6">
            <motion.span
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 text-xs font-bold uppercase tracking-[0.18em] text-[#5B2C91]"
            >
              <span className="h-2 w-2 rounded-full bg-[#D4F84A]" />
              Per i tuoi cari
            </motion.span>
            <motion.h2
              initial={{ opacity: 0, y: 22 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
              className="font-heading mt-5 text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-[#1F152E]"
              data-testid="elderly-title"
            >
              Vicino ai tuoi genitori, anche quando tu non puoi esserci
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.1 }}
              className="mt-5 max-w-xl text-base sm:text-lg leading-relaxed text-[#4A4059]"
            >
              Un infermiere qualificato raggiunge il domicilio e un medico si collega in diretta. Tu
              resti aggiornato a distanza, con la tranquillità di sapere che i tuoi genitori sono
              seguiti nel comfort di casa loro.
            </motion.p>
            <motion.button
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.2 }}
              onClick={() => openWhatsApp("Buongiorno, vorrei ricevere maggiori informazioni sul servizio TytoCare per l'assistenza medica a domicilio a un familiare.")}
              className="mt-8 inline-flex items-center justify-center gap-2 rounded-full bg-[#5B2C91] px-7 py-4 text-base font-semibold text-white shadow-[0_16px_40px_rgba(91,44,145,0.28)] hover:bg-[#4A2277] hover:-translate-y-0.5 transition-[transform,background-color] duration-200"
              data-testid="elderly-cta"
            >
              <FaWhatsapp className="h-5 w-5" />
              Organizza una visita per un familiare
            </motion.button>
          </div>
        </div>
      </div>
    </section>
  );
}
